import React from "react";
import { useNotification } from "@context/NotificationContext";
import {
  getTerminalServiceErrorMessage,
  useTerminalServiceDocumentUpload,
} from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import type { NitrogenSupplyServiceDocument } from "@services/terminal-services/types";
import {
  createNitrogenSupplyServiceReqest,
  resubmitNitrogenSupplyServiceRequest,
} from "@services/terminalServicesApi";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FOTT_SERVICE_REQUEST_FIELDS as F } from "../constants";
import { mapNitrogenSupplyFormToPayload } from "../mappers";
import type { FottServiceRequestFormData } from "../schema";

const isFileAttachment = (value: unknown): value is File =>
  typeof File !== "undefined" && value instanceof File;

const getUploadedAttachmentDocuments = (
  formData: FottServiceRequestFormData,
): NitrogenSupplyServiceDocument[] | null => {
  return (
    (formData[F.ATTACHMENT]?.filter(
      (attachment) => !isFileAttachment(attachment) && !!attachment.documentUrl,
    ) as NitrogenSupplyServiceDocument[] | undefined) ?? null
  );
};
export const useCreateNitrogenSupplyServiceRequest = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const {
    uploadEntries: attachmentFiles,
    setUploadEntries: setAttachmentFiles,
    handleUpload: handleAttachmentUpload,
    resetUpload: resetAttachmentUpload,
    isUploading: isUploadingAttachment,
  } = useTerminalServiceDocumentUpload({
    storeKey: F.ATTACHMENT,
    certificateType: "",
    fallbackFileNamePrefix: "nitrogen-supply-attachment",
    errorNotification: {
      titleKey:
        "terminal-services.fott-service-request.notifications.attachment-upload-error-title",
      descriptionKey:
        "terminal-services.fott-service-request.notifications.attachment-upload-error-description",
    },
  });

  const createRequestMutation = useMutation({
    mutationFn: createNitrogenSupplyServiceReqest,
    onSuccess: () => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-success-title"),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-error-title"),
        description:
          getTerminalServiceErrorMessage(error) ??
          t("terminal-services.fott-service-request.notifications.submit-error-description"),
        type: "error",
      });
      console.error("Create Nitrogen Supply service request failed:", error);
    },
  });

  const resubmitRequestMutation = useMutation({
    mutationFn: resubmitNitrogenSupplyServiceRequest,
    onSuccess: () => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-success-title"),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t("terminal-services.fott-service-request.notifications.submit-error-title"),
        description:
          getTerminalServiceErrorMessage(error) ??
          t("terminal-services.fott-service-request.notifications.submit-error-description"),
        type: "error",
      });

      console.error("Resubmit nitrogen supply service request failed:", error);
    },
  });

  const resubmitRequest = React.useCallback(
    async (id: string, formValues: FottServiceRequestFormData) => {
      try {
        const payload = mapNitrogenSupplyFormToPayload({
          values: formValues,
          attachmentDocuments: getUploadedAttachmentDocuments(formValues),
        });

        await resubmitRequestMutation.mutateAsync({
          id,
          payload,
        });

        return true;
      } catch {
        return false;
      }
    },
    [resubmitRequestMutation],
  );
  const submitRequest = React.useCallback(
    async (formValues: FottServiceRequestFormData) => {
      try {
        const attachmentDocuments = getUploadedAttachmentDocuments(formValues);
        const payload = mapNitrogenSupplyFormToPayload({
          values: formValues,
          attachmentDocuments,
        });

        await createRequestMutation.mutateAsync(payload);
        return true;
      } catch {
        return false;
      }
    },
    [createRequestMutation],
  );

  return {
    attachmentFiles,
    setAttachmentFiles,
    handleAttachmentUpload,
    removeAttachment: resetAttachmentUpload,
    resetAttachmentUpload,
    isUploadingAttachment,
    isSubmitting:
      createRequestMutation.isPending || resubmitRequestMutation.isPending || isUploadingAttachment,
    submitRequest,
    resubmitRequest,
  };
};
